import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import Reveal from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

const ASPECTS = ["aspect-[4/5]", "aspect-[3/4]", "aspect-[4/5]"];

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("new");
  const [products, setProducts] = useState(null);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api
      .get("/categories")
      .then((r) => setCats(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = { sort };
      if (category) params.category = category;
      if (q.trim()) params.search = q.trim();
      api
        .get("/products", { params })
        .then((r) => setProducts(r.data))
        .catch(() => setProducts([]));
    }, 250);
    return () => clearTimeout(t);
  }, [category, q, sort]);

  const setCategory = (c) => {
    if (c) setSearchParams({ category: c });
    else setSearchParams({});
  };

  return (
    <div className="pt-24 md:pt-32" data-testid="shop-page">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal>
          <p className="label-mono">The collection</p>
          <div className="mt-3 flex flex-wrap items-end justify-between gap-6">
            <h1 className="font-display text-5xl tracking-tight sm:text-6xl">
              {category || "Every piece, one of a kind."}
            </h1>
            <p className="pb-2 font-mono text-[11px] uppercase tracking-[0.2em] text-stone2" data-testid="shop-count">
              {products === null ? "Loading…" : `${products.length} ${products.length === 1 ? "piece" : "pieces"}`}
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-col gap-4 border-y border-line py-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-1 flex-nowrap gap-2 overflow-x-auto pb-1" data-testid="category-filter">
              <button
                onClick={() => setCategory("")}
                className={`shrink-0 rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 ${
                  category === "" ? "border-ink bg-ink text-parchment" : "border-stone-300 hover:border-ink"
                }`}
                data-testid="filter-all"
              >
                All
              </button>
              {cats.map((c) => (
                <button
                  key={c.name}
                  onClick={() => setCategory(c.name)}
                  className={`shrink-0 rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 ${
                    category === c.name ? "border-ink bg-ink text-parchment" : "border-stone-300 hover:border-ink"
                  }`}
                  data-testid={`filter-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <div className="relative">
                <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search pieces…"
                  className="input-base !w-48 !py-2.5 pl-9"
                  data-testid="shop-search-input"
                />
              </div>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="input-base !w-44 !py-2.5"
                data-testid="shop-sort-select"
              >
                <option value="new">Newest first</option>
                <option value="price_asc">Price: low to high</option>
                <option value="price_desc">Price: high to low</option>
                <option value="name">A to Z</option>
              </select>
            </div>
          </div>
        </Reveal>

        {products === null ? (
          <div className="grid gap-8 py-16 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-[4/5] animate-pulse bg-cream" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="py-24 text-center" data-testid="shop-empty-state">
            <p className="font-display text-3xl">Nothing on this shelf yet.</p>
            <p className="mt-3 text-sm text-stone2">Try another category, or clear your search.</p>
            <button
              onClick={() => {
                setQ("");
                setCategory("");
              }}
              className="btn-outline mt-8"
              data-testid="shop-reset-btn"
            >
              Show everything
            </button>
          </div>
        ) : (
          <div className="grid gap-x-8 gap-y-16 py-16 md:grid-cols-2 lg:grid-cols-3">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} aspect={ASPECTS[i % 3]} delay={(i % 3) * 0.07} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
